/**
 * ReviewModeBadge.jsx — Review Environment UI Components
 * 
 * Part of GRANULLO_AUTH_RBAC_SEED_V1_0.
 * Shows review mode indicators for external stakeholder accounts.
 * 
 * Components:
 * - ReviewModeBanner: Subtle watermark at top
 * - AccountRoleBadge: Shows current user role
 * - ReviewModeProvider: Context for review mode state
 */

import React, { createContext, useContext } from 'react';
import { motion } from 'framer-motion';
import { Eye, Shield, AlertTriangle } from 'lucide-react';

// Review role identification
const EXTERNAL_REVIEW_ROLES = [
  'regulator_review',
  'bank_exec',
  'bank_tech_review',
  'investor_review',
];

const ROLE_LABELS = {
  'founder_superadmin': 'Founder',
  'regulator_review': 'Regulator',
  'bank_exec': 'Bank Exec',
  'bank_tech_review': 'Bank Tech',
  'investor_review': 'Investor',
  'engineering_internal': 'Engineering',
  'internal_operator': 'Operations',
  'super_admin': 'Admin',
  'client_admin': 'Client Admin',
};

/**
 * Check if a role is an external reviewer
 */
export const isExternalReviewer = (role) => {
  return EXTERNAL_REVIEW_ROLES.includes(role?.toLowerCase());
};

/**
 * Get display label for a role
 */
export const getRoleLabel = (role) => {
  return ROLE_LABELS[role?.toLowerCase()] || role || 'User';
};

/**
 * Review Mode Context
 */
const ReviewModeContext = createContext({
  isReviewMode: false,
  role: null,
  roleLabel: 'User',
  canWrite: true,
  labsEnabled: false,
});

export const useReviewMode = () => useContext(ReviewModeContext);

/**
 * Review Mode Provider
 * Wraps app to provide review mode context
 */
export const ReviewModeProvider = ({ user, children }) => {
  const role = user?.role;
  const isReviewMode = isExternalReviewer(role);
  const canWrite = !isReviewMode && user?.write_access_enabled !== false;
  const labsEnabled = user?.labs_access_enabled === true;
  
  return (
    <ReviewModeContext.Provider value={{
      isReviewMode,
      role,
      roleLabel: getRoleLabel(role),
      canWrite,
      labsEnabled,
    }}>
      {children}
    </ReviewModeContext.Provider>
  );
};

/**
 * Review Mode Banner (Watermark)
 * Shows "Review Environment" for external reviewers
 */
export const ReviewModeBanner = ({ user }) => {
  const role = user?.role;
  
  if (!isExternalReviewer(role)) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed top-0 left-0 right-0 z-[100] bg-amber-500/10 border-b border-amber-500/30 py-1"
      data-testid="review-mode-banner"
    >
      <div className="max-w-[1200px] mx-auto px-6 flex items-center justify-center gap-2">
        <Eye className="w-3.5 h-3.5 text-amber-600" />
        <span className="text-[11px] font-medium text-amber-700">
          Review Environment
        </span>
        <span className="text-[10px] text-amber-600">
          Read only
        </span>
      </div>
    </motion.div>
  );
};

/**
 * Account Role Badge
 * Shows current user role in header
 */
export const AccountRoleBadge = ({ user, compact = false }) => {
  const role = user?.role;
  const isExternal = isExternalReviewer(role);
  const label = getRoleLabel(role);
  
  if (!user || !role) return null;
  
  return (
    <div 
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${
        isExternal 
          ? 'bg-amber-500/10 border border-amber-500/30 text-amber-700'
          : 'bg-muted border border-border text-muted-foreground'
      }`}
      data-testid="account-role-badge"
    >
      <Shield className={`w-3 h-3 ${isExternal ? 'text-amber-600' : ''}`} />
      <span className={`text-[11px] font-medium ${compact ? '' : 'hidden sm:inline'}`}>
        Role: {label}
      </span>
      {compact && (
        <span className="text-[11px] font-medium sm:hidden">{label}</span>
      )}
    </div>
  );
};

/**
 * Labs Access Warning
 * Shows when external reviewer views Labs content
 */
export const LabsAccessWarning = () => {
  return (
    <div 
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-4"
      data-testid="labs-access-warning"
    >
      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
      <div>
        <p className="text-[12px] font-medium text-amber-700">
          Non-Authoritative Content
        </p>
        <p className="text-[11px] text-amber-600">
          Labs content is experimental and not production data.
        </p>
      </div>
    </div>
  );
};

/**
 * Write Action Guard
 * Hides children for users without write access
 */
export const WriteGuard = ({ children, fallback = null }) => {
  const { canWrite } = useReviewMode();
  
  if (!canWrite) return fallback;
  return children;
};

export default ReviewModeBanner;
